import { isEqual } from 'lodash-es';
import { DateRange } from '@mui/x-date-pickers-pro';
import { tzdate, TZDate } from '@repo/tzdate';
import { useEffect, useMemo, useState, useRef, type JSX } from 'react';
import BookingBase, { BookingBaseInline } from 'src/components/domain/booking-base/BookingBase';
import BookingToggleOpenButton from 'src/components/domain/booking-widget-card/BookingToggleOpenButton';
import { useOnClickBook } from 'src/hooks/domain/activities/useOnClickBook';
import { useLocale } from '@repo/i18n';
import { Product } from '@repo/types';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useProduct } from '@repo/widget-utils/services/hooks/product';
import { updateQuantityData } from '@repo/widget-utils/price-helper';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import {
    productDisablePaymentPlans,
    productInstancesHasDepositPrices,
} from '@repo/widget-utils/cart/cartUtils';
import useTicketTypesWithinProduct from 'src/hooks/domain/cart/useTicketTypesWithinProduct';
import usePriceQuantities from 'src/hooks/domain/cart/usePriceQuantities';
import useUpdateNewCartItem from 'src/hooks/domain/cart/useUpdateNewCartItem';
import { useBookingContext } from 'src/widgets/BookingContext';
import { useNativeBookingCoverage } from 'src/hooks/domain/native-membership/useNativeBookingCoverage';
import useManageTourAndAvailability from 'src/hooks/domain/cart/useManageTourAndAvailability';
import {
    setIsLoadingPrices,
    setQuantities,
    setSelectedProducts,
    setSelectedTicketType,
    setSelectedTimeSlot,
    setSelectedProductOption,
    setHasChosenDate,
    setDefaultTicketOptions,
} from 'src/state/booking/bookingReducer';
import useSummaryPriceLines from 'src/hooks/domain/useSummaryPriceLines';
import { useActivityQuantities } from 'src/hooks/domain/booking/useActivityQuantities';
import { usePricePropsFromQuantitiesInBookingContext } from 'src/components/domain/booking-widget-card/booking-header/BookingHeader';
import { useDebounce } from 'src/hooks/common/useDebounce';
import { applyPersonsMaxLimit } from '@repo/widget-utils/booking/domains/shared/capacity-calculators';
import { mergeAccommodationSummaryLines } from 'src/components/domain/summary/accommodation-summary';
import { getNativeSummaryPriceLines } from 'src/components/domain/summary/summary-mapping';
import { createInitialCheckoutInfoData } from '@repo/widget-utils/checkout-info-helper';

interface IProps {
    productCatalogId: string;
    isAccommodation?: boolean;
    isTimeslots?: boolean;
    expandArrowInside?: boolean;
    isInlineBooking?: boolean;
    positionOffscreen?: boolean;
    dateSelectionFirst?: boolean;
}

export default function Booking(props: IProps): JSX.Element {
    const {
        productCatalogId,
        isAccommodation = false,
        isTimeslots = false,
        expandArrowInside = false,
        positionOffscreen = false,
        isInlineBooking = false,
        dateSelectionFirst = false,
    } = props;
    const { t, locale } = useLocale();
    const configuration = useConfigurations();

    const {
        quantities,
        selectedProducts,
        selectedTicketType,
        selectedProductOption,
        visible,
        bookingDispatch,
        hasChosenDate,
        isLoadingPrices,
        attemptedBooking,
        newCartItem,
    } = useBookingContext();
    const [dateRange, setDateRange] = useState<DateRange<TZDate>>([null, null]);

    // Adults (id '0') + Children (id '1') sum to the `guests` query-param sent
    // to Visbook (Visbook has no per-segment pricing — the breakdown is preserved
    // on the operator side via the Voksne/Barn note appended in
    // getAccommodationsFromCart). Debounced 400ms.
    const accommodationGuests = isAccommodation
        ? (quantities.find((q) => q.id === '0')?.quantity ?? 0) +
              (quantities.find((q) => q.id === '1')?.quantity ?? 0) || 1
        : undefined;
    const debouncedAccommodationGuests = useDebounce(accommodationGuests, 400);

    // Only refetch with dates once the user has committed their calendar choice
    // by clicking the OK button. That dispatch populates `selectedProducts` in
    // BookingContext (see CalendarPopover's onOkClicked → setSelectedProducts).
    // Intermediate mui range changes only update local `dateRange` state and
    // must not trigger fetches. Picking a new date after a commit clears
    // selectedProducts (via the "Tøm"/Clear button or a reset flow), which
    // reverts us to the dateless initial response.
    const hasCommittedDateRange = Boolean(
        isAccommodation && selectedProducts && selectedProducts.length > 0,
    );
    const committedDateRange: DateRange<TZDate> | undefined =
        hasCommittedDateRange && dateRange[0] && dateRange[1] ? dateRange : undefined;

    // Before dates are committed, don't let guest ticks trigger fetches —
    // the dateless response is identical regardless of guests, so changing
    // that value pre-commit would just churn the SWR cache key.
    const effectiveGuests = committedDateRange ? debouncedAccommodationGuests : undefined;

    const { data: product, isLoading: isLoadingProduct } = useProduct(
        productCatalogId.toString(),
        isAccommodation ? 'accommodation' : isTimeslots ? 'timeslot' : undefined,
        committedDateRange,
        effectiveGuests,
    );

    // True while a selection change is in the debounce window (user has moved the
    // picker but the refetch hasn't fired yet) — used to show the price skeleton
    // so we don't display a stale number between the click and the network response.
    // Only meaningful once dates are committed, since pre-commit picker changes
    // don't trigger fetches at all (see effectiveGuests above).
    const isRefetchPending =
        isAccommodation &&
        !!committedDateRange &&
        debouncedAccommodationGuests !== accommodationGuests;

    useEffect(() => {
        if (!isAccommodation) return;
        bookingDispatch(setIsLoadingPrices(isLoadingProduct || isRefetchPending));
    }, [isAccommodation, isLoadingProduct, isRefetchPending, bookingDispatch]);

    // Visbook returns an empty prices[] when no price bracket matches the requested
    // units/guests config (e.g. 3 rooms × 3 guests when only single-room bookings
    // are allowed). Only treat it as "unavailable" once the user has chosen a date —
    // before that, the initial dateless fetch is expected to populate the price-type
    // dropdown from all configured prices.
    const accommodationUnavailable = Boolean(
        isAccommodation &&
            dateRange[0] &&
            dateRange[1] &&
            product &&
            (product.ticketOptions[0]?.ticketTypes?.length ?? 0) === 0,
    );

    const hasDepositPrices = selectedProducts
        ? productInstancesHasDepositPrices(selectedProducts)
        : false;
    const newItemDisablePaymentPlans = product
        ? productDisablePaymentPlans(
              product,
              false,
              hasDepositPrices,
              configuration.disableMembershipBooking,
          )
        : false;
    useUpdateNewCartItem(newItemDisablePaymentPlans);

    const {
        nextProductAvailabilityStart,
        availabilities,
        availabilitySearchPeriod,
        setAvailabilitySearchPeriod,
        isLoading: isLoadingAvailabilities,
    } = useManageTourAndAvailability(product, productCatalogId);

    // Update selected products so that they are translated when locale changes
    useEffect(() => {
        const updatedProducts = selectedProducts?.map(
            (product) => availabilities?.find((avail) => avail.id === product.id) ?? product,
        );

        if (isEqual(updatedProducts, selectedProducts)) return;

        bookingDispatch(setSelectedProducts(updatedProducts));
    }, [locale, availabilities, selectedProducts, bookingDispatch]);

    useWidgetEventEffect(
        (productCatalog) => ({
            eventType: 'viewItem',
            product: productCatalog,
            productType: productCatalog.type,
        }),
        product,
        visible,
    );

    const ticketTypes = useTicketTypesWithinProduct(product);
    const priceQuantities = usePriceQuantities(product, selectedProducts, quantities);

    const numPrices = product?.ticketOptions.length;

    function noExtendSetDateRange(newRange: DateRange<TZDate>) {
        // Removes the ability to extend range in both directions in mui calendar
        if (dateRange[0]?.format('YYYY-MM-DD') !== newRange[0]?.format('YYYY-MM-DD')) {
            newRange[1] = null;
        }
        setDateRange(newRange);
    }

    useEffect(() => {
        const updatedQuantities = updateQuantityData(
            selectedProducts ?? [],
            product?.ticketOptions ?? [],
            quantities,
        );

        if (isEqual(updatedQuantities, quantities)) return;

        bookingDispatch(setQuantities(updatedQuantities));
    }, [selectedProducts, product, quantities, bookingDispatch]);

    // Sync selectedTicketType whenever the product refetches. For accommodation,
    // changing rooms/guests causes Visbook to return new ticketType prices even
    // though the ticketType.id stays stable — we must replace the stored ticket
    // type (and its .price) so the header total reflects the latest response.
    // Guard with isEqual to avoid a dispatch-loop when the effect re-runs after
    // it writes selectedTicketType (since the new reference itself re-triggers).
    useEffect(() => {
        const next =
            ticketTypes.length === 1
                ? ticketTypes[0]
                : ticketTypes.length > 0 && selectedTicketType
                  ? ticketTypes.find((t) => t.id === selectedTicketType.id)
                  : undefined;
        if (!isEqual(next, selectedTicketType)) {
            bookingDispatch(setSelectedTicketType(next));
        }
    }, [ticketTypes, selectedTicketType, bookingDispatch]);

    // Set default ticket options when product loads (for showing unavailable tickets)
    useEffect(() => {
        if (product?.ticketOptions) {
            bookingDispatch(setDefaultTicketOptions(product.ticketOptions));
        }
    }, [product?.ticketOptions, bookingDispatch]);

    // Auto-select first product option when product loads (OCTO only)
    useEffect(() => {
        const productOptions = product?.productOptions;
        if (!productOptions?.length) return;

        // Only auto-select if no option is currently selected
        if (!selectedProductOption) {
            bookingDispatch(setSelectedProductOption(productOptions[0]));
        }
    }, [product?.productOptions, selectedProductOption, bookingDispatch]);

    // Track previous product option for quantity preservation
    const prevProductOptionRef = useRef(selectedProductOption);

    // Handle product option switch: reset calendar selection and preserve quantities
    useEffect(() => {
        const prevOption = prevProductOptionRef.current;
        prevProductOptionRef.current = selectedProductOption;

        // Skip if no change or no previous option (first selection)
        if (!prevOption || !selectedProductOption || prevOption.id === selectedProductOption.id) {
            return;
        }

        // Reset calendar/availability selections - they're no longer valid for the new option
        bookingDispatch(setSelectedProducts(undefined));
        bookingDispatch(setSelectedTimeSlot(undefined));
        bookingDispatch(setHasChosenDate(false));

        // Preserve quantities by matching unitType between options
        const prevOptionTickets = quantities.filter((q) => q.productOptionId === prevOption.id);
        const newOptionTickets = quantities.filter(
            (q) => q.productOptionId === selectedProductOption.id,
        );

        if (prevOptionTickets.length === 0 || newOptionTickets.length === 0) {
            return;
        }

        // Map quantities from prev to new option by matching unitType
        const updatedQuantities = quantities.map((ticket) => {
            // Only update tickets from the new option
            if (ticket.productOptionId !== selectedProductOption.id) {
                return ticket;
            }

            // Find matching ticket from previous option by unitType
            const matchingPrevTicket = prevOptionTickets.find(
                (prev) => prev.unitType === ticket.unitType,
            );

            if (matchingPrevTicket && matchingPrevTicket.quantity > 0) {
                return { ...ticket, quantity: matchingPrevTicket.quantity };
            }

            return ticket;
        });

        if (!isEqual(updatedQuantities, quantities)) {
            bookingDispatch(setQuantities(updatedQuantities));
        }
    }, [selectedProductOption, quantities, bookingDispatch]);

    const onClickBook = useOnClickBook(priceQuantities, product, setDateRange);

    // if "god mode" is enabled, allow booking from one year ago
    // otherwise, booking can start from the next available date
    const minBookingDate = configuration.enableGodMode
        ? tzdate().add(-1, 'year')
        : nextProductAvailabilityStart;

    // For accommodation, Visbook returns a single total per booking on the
    // selected ticketType. Each ticket option (Voksne, Barn) with quantity > 0
    // would otherwise produce its own summary line at that full total — duplicating
    // the price. Collapse to one line and override the label below to surface
    // the Voksne / Barn breakdown.
    const quantitiesForSummary = hasChosenDate
        ? isAccommodation
            ? priceQuantities.slice(0, 1).map((q) => ({ ...q, quantity: 1 }))
            : priceQuantities
        : [];
    // Native member-card preview: drives the discounted total in the card header (money path only)
    // and the per-line coverage in the body. Inert for guest/legacy / non-money currency types.
    const {
        result: bookingCoverageResult,
        discountedTotal,
        isLoading: isCoverageLoading,
    } = useNativeBookingCoverage();

    const isDetailedLines = false;
    const rawSummaryLines = useSummaryPriceLines(
        quantitiesForSummary,
        isDetailedLines,
        isLoadingPrices || isCoverageLoading,
        selectedTicketType,
    );
    const summaryLines = useMemo(() => {
        // Native membership: per-line member-card coverage for the configured (pre-cart) booking.
        if (bookingCoverageResult && newCartItem) {
            const ci = createInitialCheckoutInfoData(null, [newCartItem], null);
            const nativeLines = ci
                ? getNativeSummaryPriceLines(
                      newCartItem,
                      bookingCoverageResult,
                      ci,
                      locale,
                      t,
                      isLoadingPrices,
                  )
                : null;
            if (nativeLines) return nativeLines;
        }
        if (!isAccommodation) return rawSummaryLines;
        return mergeAccommodationSummaryLines(rawSummaryLines, quantities, t);
    }, [
        isAccommodation,
        rawSummaryLines,
        quantities,
        t,
        locale,
        isLoadingPrices,
        bookingCoverageResult,
        newCartItem,
    ]);

    // Use new domain hook for activity quantities
    const {
        inputItems: rawInputItems,
        errors,
        onChangeQuantity,
        disabledMessage,
    } = useActivityQuantities();

    // For accommodation, clamp the SUM of Voksne + Barn against the selected
    // price's guest-bracket with a fallback chain when brackets aren't set on
    // the Visbook price:
    //   1. selectedTicketType.maxGuests (Visbook price's persons_limit2)
    //   2. product.capacity (per-unit persons max, Visbook attribute group 1772)
    //   3. config.personsMax (tenant-level cap)
    //   4. 20 as a hard upper bound
    // applyPersonsMaxLimit distributes the cap across the inputs so each input's
    // max = totalCap - other_input.value, keeping the sum bounded.
    const inputItems = useMemo(() => {
        if (!isAccommodation) return rawInputItems;
        const resolvedMaxGuests =
            selectedTicketType?.maxGuests || product?.capacity || configuration.personsMax || 20;
        return applyPersonsMaxLimit(rawInputItems, resolvedMaxGuests);
    }, [
        isAccommodation,
        rawInputItems,
        selectedTicketType?.maxGuests,
        product?.capacity,
        configuration.personsMax,
    ]);

    // Split errors for BookingBase compatibility
    const quantityErrors = errors.filter((e) => e.type === 'individual');
    const totalErrors = errors.filter((e) => e.type === 'total');

    // Calculate button state and warning label
    const disableBookButton =
        (!hasChosenDate && attemptedBooking) || errors.length > 0 || accommodationUnavailable;

    function getWarningLabel() {
        if (!hasChosenDate) return undefined;
        if (accommodationUnavailable) {
            return t.no_availability_for_selected_rooms_and_guests;
        }
        if (errors.some((e) => e.error === 'minEntrants')) {
            return t.a_minimum_of_participants_is_required_to_book_this_product;
        }
        if (errors.some((e) => e.error === 'capacity')) {
            return t.no_available_capacity_for_this_tour;
        }
        return undefined;
    }

    const warningLabel = getWarningLabel();

    const productFromPrice = product?.fromPrice;

    const bookingHeaderPriceProps = usePricePropsFromQuantitiesInBookingContext(
        priceQuantities,
        productFromPrice,
    );

    const finalHeaderPriceProps = useMemo(() => {
        if (!bookingHeaderPriceProps || bookingHeaderPriceProps.currencyType !== 'money') {
            return bookingHeaderPriceProps;
        }
        if (discountedTotal === null && !isCoverageLoading) return bookingHeaderPriceProps;
        return {
            ...bookingHeaderPriceProps,
            isLoadingPrices: bookingHeaderPriceProps.isLoadingPrices || isCoverageLoading,
            totalPrice: discountedTotal ?? bookingHeaderPriceProps.totalPrice,
        };
    }, [bookingHeaderPriceProps, discountedTotal, isCoverageLoading]);

    if (isInlineBooking) {
        return (
            <BookingBaseInline
                productTitle={product?.title ?? ''}
                travelerQuantities={quantities}
                numPrices={numPrices}
                availabilityData={availabilities}
                rightButtonLabel={capitalize(t.book_now)}
                onClickRightButton={onClickBook}
                availabilitySearchPeriod={availabilitySearchPeriod}
                setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
                title={capitalize(t.book_now)}
                onSelectDateRange={noExtendSetDateRange}
                dateRangeVariant={getDateRangeVariant(product)}
                selectedDateRange={dateRange}
                ticketTypes={ticketTypes}
                productOptions={product?.productOptions}
                minDate={minBookingDate}
                isLoadingAvailabilities={isLoadingAvailabilities}
                dateSelectionFirst={dateSelectionFirst}
                summaryLines={summaryLines}
                disableBookButton={disableBookButton}
                quantityErrors={quantityErrors}
                totalErrors={totalErrors}
                warningLabel={warningLabel}
                quantityInputItems={inputItems}
                onChangeQuantity={onChangeQuantity}
                disabledMessage={disabledMessage}
            />
        );
    }

    return (
        <BookingBase
            hideLeftButton={true}
            productTitle={product?.title ?? ''}
            travelerQuantities={quantities}
            numPrices={numPrices}
            availabilityData={availabilities}
            rightButtonLabel={capitalize(t.book_now)}
            onClickRightButton={onClickBook}
            availabilitySearchPeriod={availabilitySearchPeriod}
            setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
            title={capitalize(t.book_now)}
            expandArrowInside={expandArrowInside}
            onSelectDateRange={noExtendSetDateRange}
            dateRangeVariant={getDateRangeVariant(product)}
            selectedDateRange={dateRange}
            toggleButton={
                <BookingToggleOpenButton expandArrowInside={expandArrowInside} visible={visible} />
            }
            positionOffscreen={positionOffscreen}
            ticketTypes={ticketTypes}
            productOptions={product?.productOptions}
            minDate={minBookingDate}
            isLoadingAvailabilities={isLoadingAvailabilities}
            dateSelectionFirst={dateSelectionFirst}
            summaryLines={summaryLines}
            disableBookButton={disableBookButton}
            quantityErrors={quantityErrors}
            totalErrors={totalErrors}
            warningLabel={warningLabel}
            quantityInputItems={inputItems}
            onChangeQuantity={onChangeQuantity}
            bookingHeaderPriceProps={finalHeaderPriceProps}
            disabledMessage={disabledMessage}
        />
    );
}

export function getDateRangeVariant(product: Product | null) {
    return product?.type === 'nights' || product?.type === 'accommodation'
        ? 'nights'
        : product?.type === 'days'
          ? 'days'
          : undefined;
}
